import {
  addDays,
  addWeeks,
  endOfDay,
  isAfter,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { RRule } from 'shared/lib/rrule';
import { HabitSchedule, ScheduleEntryState } from 'shared/types/habit-schedule';
import { getScheduleInterval } from 'shared/utils/get-schedule-interval';

import { frequencyToWeekAdjustmentsMap } from './constants';
import { getActualScheduleStartDate } from './get-actual-schedule-start-date';
import { Options } from './types';

export const isScheduleOverdue = (
  schedule: HabitSchedule,
  { today }: Options,
) => {
  // with a count it cannot be overdue
  if (schedule.frequency?.count) {
    return false;
  }

  // completion counts higher than 1 means it cannot be overdue
  if (schedule.completionTarget.count > 1) {
    return false;
  }

  // check if startDate is in the future
  const startDate = getActualScheduleStartDate(schedule);

  const dayEnd = endOfDay(today);

  // check if the last skip is in the future
  const meaningfulEntries = schedule.entries.filter(
    ({ state, value, date }) =>
      !isAfter(date, dayEnd) &&
      ((state === ScheduleEntryState.Complete && !!value) ||
        state !== ScheduleEntryState.Complete),
  );
  const lastEntryDate = meaningfulEntries.length
    ? new Date(Math.max(...meaningfulEntries.map(({ date }) => date.getTime())))
    : undefined;

  // check if there are any uncompleted planned occurrences in the past
  const pastUncompletedPlannedOccurrence = schedule.plannedOccurrences
    ?.filter(
      (po) =>
        isAfter(today, po.date) &&
        !isSameDay(po.date, today) &&
        (!lastEntryDate || isAfter(po.date, lastEntryDate)),
    )
    .sort((a, b) => b.date.getTime() - a.date.getTime())[0];

  if (pastUncompletedPlannedOccurrence) {
    return endOfDay(pastUncompletedPlannedOccurrence.date);
  }

  if (isAfter(startDate, today) || isSameDay(startDate, today)) {
    return false;
  }

  if (
    lastEntryDate &&
    (isAfter(lastEntryDate, today) || isSameDay(lastEntryDate, today))
  ) {
    return false;
  }

  // create a rrule out of the schedule data
  const rrule = new RRule({
    ...RRule.fromString(schedule.rrule.format).origOptions,
    dtstart: startOfDay(schedule.startDate),
  });

  const interval = getScheduleInterval(schedule);

  // get the latest activity within the schedule to start looking from
  const latestScheduleDate = [
    // currentTimeframeStartDate,
    lastEntryDate ? startOfDay(addDays(lastEntryDate, 1)) : undefined,
    startOfDay(startDate),
    addWeeks(
      endOfDay(today),
      interval * frequencyToWeekAdjustmentsMap[rrule.options.freq],
    ),
  ]
    .filter(Boolean)
    .sort((dateA, dateB) => dateB!.getTime() - dateA!.getTime())[0] as Date;

  const lastDueDate = rrule
    .between(endOfDay(addDays(latestScheduleDate, -1)), today, true)
    .pop();

  if (!lastDueDate || isSameDay(lastDueDate, today)) {
    return false;
  }

  return endOfDay(lastDueDate);
};
